<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Icon from './ui/Icon.vue'
import Kbd from './ui/Kbd.vue'
import { ariaCombo, keysOf } from '../lib/shortcuts'
import FilterBar from './FilterBar.vue'
import RangePicker from './RangePicker.vue'
import { state, openSheet } from '../stores/state'
import { setTheme } from '../stores/theme'
import { cyclePrivacy } from '../stores/settings'
import { openHelp } from '../stores/help'
import { exportCsv } from '../stores/export'
import { t } from '../stores/i18n'

const PRIVACY = {
  off: { icon: 'eye', tone: '' },
  on: { icon: 'eye-off', tone: '' },
  strict: { icon: 'eye-off', tone: 'text-danger' },
}
const privacy = computed(() => {
  const mode = PRIVACY[state.settings.privacy] ? state.settings.privacy : 'off'
  return { ...PRIVACY[mode], label: t(`privacy.${mode}`) }
})

const dark = computed(() => state.settings.theme !== 'light')

const toggleTheme = () => setTheme(dark.value ? 'light' : 'dark')

/**
 * Below `md` the header keeps only what is used most — the range and Settings
 * — and the rest goes behind one button, each with its name beside its icon:
 * seven icons in a row on a phone either wrapped the header onto a second
 * line or had to shrink until nobody could hit them. From `md` up there is
 * room, and they sit in the row as icons again.
 */
const menuOpen = ref(false)
const menu = ref(null)
const MENU = computed(() => [
  // The ledger in and out: a bank statement in, the view out as a spreadsheet.
  // `key` is also the shortcut's id (lib/shortcuts.js): each shows its key.
  { key: 'import', icon: 'upload', label: t('header.import'), run: () => openSheet('import') },
  { key: 'export', icon: 'download', label: t('header.export'), run: exportCsv },
  { key: 'tags', icon: 'tag', label: t('charts.tags'), run: () => openSheet('tags') },
  { key: 'categories', icon: 'folder', label: t('charts.categories'), run: () => openSheet('categories') },
  // What keeping money has earned: saving, earning, finishing goals.
  { key: 'achievements', icon: 'trophy', label: t('header.achievements'), run: () => openSheet('achievements') },
  { key: 'console', icon: 'terminal', label: t('header.console'), run: () => openSheet('console') },
  { key: 'privacy', icon: privacy.value.icon, tone: privacy.value.tone, label: privacy.value.label, run: cyclePrivacy, stay: true },
  { key: 'theme', icon: dark.value ? 'sun' : 'moon', label: dark.value ? t('header.lightTheme') : t('header.darkTheme'), run: toggleTheme, stay: true },
  { key: 'help', icon: 'help', label: t('header.help'), run: openHelp },
])

/**
 * A row's name, and a line under it: a label that carries its explanation
 * after a dash ("Privacy off — everything visible") is split there, so the
 * state reads as the name and the rest does not cut it off.
 */
const parts = (label) => {
  const at = label.indexOf(' — ')
  return at === -1 ? { name: label, hint: '' } : { name: label.slice(0, at), hint: label.slice(at + 3) }
}

/** Privacy and the theme are switches: the menu stays open to show what they did. */
function pick(item) {
  item.run()
  if (!item.stay) menuOpen.value = false
}

// Closed by a tap anywhere else, or by Escape.
const outside = (event) => {
  if (menu.value && !menu.value.contains(event.target)) menuOpen.value = false
}
const escape = (event) => event.key === 'Escape' && (menuOpen.value = false)
watch(menuOpen, (open) => {
  if (typeof document === 'undefined') return
  const act = open ? 'addEventListener' : 'removeEventListener'
  document[act]('pointerdown', outside)
  document[act]('keydown', escape)
})
onBeforeUnmount(() => (menuOpen.value = false))
</script>

<template>
  <!-- Not sticky itself: App.vue's top bar is, with the banner above this. -->
  <header class="border-b border-line-soft">
    <div class="mx-auto w-full max-w-[1400px] px-3 pt-3 sm:px-5">
      <div class="flex items-center justify-end gap-1 sm:gap-3">
        <!-- The wordmark, in the ink that reads on the theme, at every width — it
             fits a phone's row too. Bound rather than written as a plain src so
             Vite leaves the public/ paths alone. -->
        <h1 class="min-w-fit flex-1">
          <img
            :src="dark ? 'okane_white.png' : 'okane_black.png'"
            alt="OKANE"
            width="714"
            height="208"
            class="block h-7 w-auto"
          />
        </h1>

        <!-- The window the whole screen looks through, among the controls that
             change what the screen is rather than what is in it. -->
        <RangePicker />

        <!-- From `md` up, the rest in the row as icons. What a transaction *is*
             — its category, its tags — sits beside the console rather than
             three taps into Settings: they are edited while filing. -->
        <div class="hidden items-center gap-1 md:flex lg:gap-2">
          <button
            v-for="item in MENU"
            :key="item.key"
            class="btn-ghost !gap-1 !px-1.5 lg:!px-2"
            :aria-label="item.label"
            :aria-keyshortcuts="ariaCombo(keysOf(item.key))"
            :title="item.label"
            @click="item.run()"
          >
            <!-- The tone goes on the icon: `button { color: inherit }` in the base
                 layer outranks any colour utility on the button itself. -->
            <Icon :name="item.icon" :size="19" :class="item.tone" />
            <Kbd :combo="keysOf(item.key)" />
          </button>
        </div>

        <!-- Below `md`, behind one button. -->
        <div ref="menu" class="relative md:hidden">
          <button
            class="btn-ghost !px-2"
            :aria-label="t('header.menu')"
            :aria-expanded="menuOpen"
            aria-haspopup="menu"
            @click="menuOpen = !menuOpen"
          >
            <Icon :name="menuOpen ? 'x' : 'menu'" :size="20" />
          </button>
          <div
            v-if="menuOpen"
            class="absolute top-full right-0 z-40 mt-2 w-60 rounded-xl border border-line bg-surface p-1.5
                   shadow-[var(--c-shadow)]"
            role="menu"
            data-header-menu
          >
            <button
              v-for="item in MENU"
              :key="item.key"
              class="menu-row w-full justify-start gap-3"
              role="menuitem"
              @click="pick(item)"
            >
              <Icon :name="item.icon" :size="18" class="shrink-0" :class="item.tone || 'text-muted'" />
              <span class="min-w-0 flex-1 text-left">
                <span class="block truncate">{{ parts(item.label).name }}</span>
                <span v-if="parts(item.label).hint" class="block text-[12px] leading-snug text-faint">
                  {{ parts(item.label).hint }}
                </span>
              </span>
              <Kbd :combo="keysOf(item.key)" />
            </button>
          </div>
        </div>

        <button
          class="btn-ghost !gap-1 !px-2"
          :aria-label="t('header.settings')"
          :aria-keyshortcuts="ariaCombo(keysOf('settings'))"
          :title="t('header.settings')"
          @click="openSheet('settings')"
        >
          <Icon name="settings" :size="19" />
          <Kbd :combo="keysOf('settings')" />
        </button>
      </div>

      <div class="mt-3 pb-3">
        <FilterBar />
      </div>
    </div>
  </header>
</template>
